import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Collection } from "../const/collections";
import { BonCommande } from "../models/bon-commande";
import { BonLivraison, BonCommandeReliquats, FichierExport, ReceptionPayload } from "../models/bon-livraison";
import { DocumentHistorique, DocumentHistoriqueType } from "../models/document-historique";

/**
 * Réceptions des achats : BC à réceptionner, saisie et validation des BL, export BL.txt vers Sage.
 * Toute écriture passe par les routes dédiées du backend (services/reception.js), jamais par le create/update standard.
 */
@Injectable({ providedIn: "root" })
export class ReceptionService {
	private apiURL = environment.apiURL;

	constructor(private http: HttpClient) {}

	// --- Bons de commande ---

	/** Le BC et ses lignes avec commandé, déjà reçu, en brouillon et reliquat. `exclureBl` : le brouillon en cours d'édition. */
	getReliquats(bcId: number, exclureBl?: number): Observable<{ data: BonCommandeReliquats }> {
		let params = new HttpParams();
		if (exclureBl) params = params.set("exclureBl", String(exclureBl));
		return this.http.get<{ data: BonCommandeReliquats }>(`${this.apiURL}/${Collection.BON_COMMANDE}/${bcId}/reliquats`, { params });
	}

	solder(bcId: number, motif: string): Observable<{ data: BonCommandeReliquats }> {
		return this.http.put<{ data: BonCommandeReliquats }>(`${this.apiURL}/${Collection.BON_COMMANDE}/${bcId}/solder`, { data: { motif } });
	}

	desolder(bcId: number): Observable<{ data: BonCommandeReliquats }> {
		return this.http.put<{ data: BonCommandeReliquats }>(`${this.apiURL}/${Collection.BON_COMMANDE}/${bcId}/desolder`, {});
	}

	/** Correction d'une ligne de BC, quantité et/ou prix (administrateur). C'est ce qui permet de recevoir plus que la commande. */
	modifierLigneCommande(bcId: number, ligneId: number, valeurs: { qte?: number | null; prix?: number | null }, motif: string): Observable<{ data: BonCommandeReliquats }> {
		return this.http.put<{ data: BonCommandeReliquats }>(`${this.apiURL}/${Collection.BON_COMMANDE}/${bcId}/lignes/${ligneId}`, { data: { ...valeurs, motif } });
	}

	/** Changement du fournisseur d'un BC (administrateur). Refusé dès qu'un BL porte sur ce BC. */
	modifierFournisseurCommande(bcId: number, fournisseur: number, motif: string): Observable<{ data: BonCommandeReliquats }> {
		return this.http.put<{ data: BonCommandeReliquats }>(`${this.apiURL}/${Collection.BON_COMMANDE}/${bcId}/fournisseur`, { data: { fournisseur, motif } });
	}

	/**
	 * BC ouverts d'un fournisseur : les partiels (ils ont déjà un BL dans GP), et les non réceptionnés
	 * (y compris sans statut, antérieurs au chantier) à condition de ne pas avoir été exportés vers Sage.
	 */
	getBonsCommandesOuverts(fournisseurId: number): Observable<{ data: BonCommande[] }> {
		const params = new HttpParams()
			.set("filters[fournisseur][id][$eq]", String(fournisseurId))
			.set("filters[$or][0][statutReception][$eq]", "partiellement_receptionne")
			.set("filters[$or][1][$and][0][$or][0][statutReception][$eq]", "non_receptionne")
			.set("filters[$or][1][$and][0][$or][1][statutReception][$null]", "true")
			.set("filters[$or][1][$and][1][$or][0][isExporte][$eq]", "false")
			.set("filters[$or][1][$and][1][$or][1][isExporte][$null]", "true")
			.set("populate[consultation][fields][0]", "numero")
			.set("populate[articles][fields][0]", "qte")
			.set("populate[articles][fields][1]", "prix")
			.set("sort", "id:desc")
			.set("pagination[pageSize]", "500");
		return this.http.get<{ data: BonCommande[] }>(`${this.apiURL}/${Collection.BON_COMMANDE}`, { params });
	}

	// --- Bons de livraison ---

	/** Détail complet d'un BL, avec la distribution de chaque ligne par demande et par projet. */
	getBonLivraison(id: number): Observable<{ data: BonLivraison }> {
		return this.http.get<{ data: BonLivraison }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/distribution`);
	}

	creer(payload: ReceptionPayload): Observable<{ data: BonLivraison }> {
		return this.http.post<{ data: BonLivraison }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/receptions`, { data: payload });
	}

	modifier(id: number, payload: Partial<ReceptionPayload>): Observable<{ data: BonLivraison }> {
		return this.http.put<{ data: BonLivraison }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/reception`, { data: payload });
	}

	valider(id: number): Observable<{ data: BonLivraison }> {
		return this.http.put<{ data: BonLivraison }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/valider`, {});
	}

	annuler(id: number, motif: string): Observable<{ data: BonLivraison }> {
		return this.http.put<{ data: BonLivraison }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/annuler`, { data: { motif } });
	}

	// --- Export BL.txt ---

	countNonExported(): Observable<{ data: { count: number } }> {
		return this.http.get<{ data: { count: number } }>(`${this.apiURL}/${Collection.BON_LIVRAISON}/count-non-exported`);
	}

	/** Sans `ids`, exporte tous les BL validés. */
	exporter(ids?: number[]): Observable<FichierExport> {
		return this.http.put<FichierExport>(`${this.apiURL}/${Collection.BON_LIVRAISON}/export`, ids?.length ? { ids } : {});
	}

	exporterParId(id: number): Observable<FichierExport> {
		return this.http.put<FichierExport>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/export-by-id`, {});
	}

	/** Régénère le fichier d'un BL déjà exporté, sans rien modifier. Réservé à admin et magasinierinformatique. */
	reexporter(id: number): Observable<FichierExport> {
		return this.http.put<FichierExport>(`${this.apiURL}/${Collection.BON_LIVRAISON}/${id}/reexport`, {});
	}

	/** Le fichier n'est jamais écrit sur le serveur : le navigateur le télécharge. */
	telechargerFichier(fichier: FichierExport): void {
		const blob = new Blob([fichier.content], { type: "text/plain" });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fichier.filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}

	// --- Historique ---

	getHistorique(documentType: DocumentHistoriqueType, documentId: number): Observable<{ data: DocumentHistorique[]; meta: any }> {
		const params = new HttpParams()
			.set("filters[documentType][$eq]", documentType)
			.set("filters[documentId][$eq]", String(documentId))
			.set("populate[user][fields][0]", "nom")
			.set("populate[user][fields][1]", "username")
			.set("sort", "id:desc")
			.set("pagination[pageSize]", "200");
		return this.http.get<{ data: DocumentHistorique[]; meta: any }>(`${this.apiURL}/${Collection.DOCUMENT_HISTORIQUE}`, { params });
	}
}
